/**
 * Login return-to primitive (shared).
 *
 * The app already had ONE place that knew how to send a user to the login page
 * and bring them back afterwards: the axios response interceptor in
 * `src/services/request.js`, which builds `"/login?redirect=<encoded path>"`
 * (`src/pages/Auth/Login` reads that `redirect` param in `getRedirectPath`).
 * Every OTHER caller that needed the same thing hand-rolled a bare
 * `nav("/login")` and silently dropped the page the visitor was on.
 *
 * This module is that primitive, extracted so there is a single implementation
 * to reason about (and to test). Behaviour is intentionally IDENTICAL to the
 * interceptor's previous inline logic — same rule, same encoding — so lifting
 * the interceptor onto it is a no-op for existing flows.
 *
 * Rules:
 *  - the current location is preserved as `?redirect=<encodeURIComponent(path)>`,
 *  - EXCEPT when it is meaningless to come back to: "/" or the login page
 *    itself (that would bounce a visitor in a loop),
 *  - the return target keeps its query string and hash, because both carry real
 *    state in this app (e.g. `?unlock=success`, invite `?token=`).
 */
import { PATH } from "./config";

/**
 * Build the login URL that returns the visitor to `pathname` after they sign in.
 *
 * @param {string} [pathname] e.g. "/wheel/512"
 * @param {string} [search]   e.g. "?foo=1" ("" when none)
 * @param {string} [hash]     e.g. "#seg-3" ("" when none)
 * @returns {string} "/login" or "/login?redirect=%2Fwheel%2F512"
 */
export function buildLoginRedirect(pathname, search, hash) {
  const currentPath = `${pathname || ""}${search || ""}${hash || ""}`;
  const keep =
    currentPath && currentPath !== "/" && currentPath !== PATH.LOGIN;
  return keep
    ? `${PATH.LOGIN}?redirect=${encodeURIComponent(currentPath)}`
    : PATH.LOGIN;
}

/**
 * Same as {@link buildLoginRedirect} but reads the live browser location.
 * Safe to call in a non-browser (test/SSR) context — falls back to "/login".
 *
 * @returns {string}
 */
export function buildLoginRedirectFromLocation() {
  if (typeof window === "undefined" || !window.location) return PATH.LOGIN;
  const { pathname, search, hash } = window.location;
  return buildLoginRedirect(pathname, search, hash);
}

/**
 * BUG #700 (Gordon 2026-08-22): the account switcher's EXPIRED-token path used
 * to show a vague `toast.info("Please sign in again to switch to X.")` and drop
 * the user on a plain login page with no explanation of WHY they were bounced.
 *
 * This builds the re-authentication login URL for that path: it prefills the
 * remembered account's email (so the visitor does not retype it) AND stamps
 * `reason=session-expired`, which the Login page reads to render an explanatory
 * banner ("Your session for <email> expired — please sign in again") instead of
 * the vague toast. Email is optional — an account with no remembered email still
 * gets the explained re-auth page, just without the prefill.
 *
 * @param {string} [email] the remembered account's email ("" when unknown)
 * @returns {string} e.g. "/login?email=a%40b.com&reason=session-expired"
 */
export function buildReauthLoginRedirect(email) {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  params.set("reason", "session-expired");
  return `${PATH.LOGIN}?${params.toString()}`;
}

/**
 * BUG #700 generic mid-session expiry (Gordon 2026-08-23, AskUserQuestion:
 * "cover generic expiry too"): #4430 gave the friendly re-auth banner only to
 * the account-switcher path; a GENERIC mid-session expiry (the request.js 401
 * "Invalid token." interceptor) still dropped the user on a bare `/login` with
 * no explanation. This is the single decision that path now makes:
 *
 *  - if we KNOW who was signed in — a remembered email captured from the auth
 *    store BEFORE it was cleared — send them to the explained re-auth page
 *    ({@link buildReauthLoginRedirect}: `reason=session-expired` + email
 *    prefill), so the expiry is explained and they do not retype their address;
 *  - otherwise (no remembered email — e.g. a never-authenticated caller) fall
 *    back to the normal return-to login URL
 *    ({@link buildLoginRedirectFromLocation}), which is byte-identical to the
 *    interceptor's previous behaviour.
 *
 * Kept as a pure function of `email` (not reading storage itself) so the
 * known-vs-unknown decision is unit-testable without a browser.
 *
 * @param {string} [email] remembered email ("" / undefined when unknown)
 * @returns {string}
 */
export function buildExpiryLoginRedirect(email) {
  return email
    ? buildReauthLoginRedirect(email)
    : buildLoginRedirectFromLocation();
}

/**
 * The other half of the round trip: what the login page is allowed to send the
 * visitor BACK to once they have signed in.
 *
 * `getRedirectPath` in `src/pages/Auth/Login/index.jsx` used `?redirect=`
 * verbatim. Because the parameter is attacker-controlled (it is just a query
 * string on a public URL), that made the login page an open redirect: a
 * phishing link `"/login?redirect=//evil.example"` sends the visitor off-site
 * the instant they authenticate — at the exact moment they trust the page most.
 * `"javascript:..."` is the same problem in script form.
 *
 * The rule is deliberately strict, because everything this app legitimately
 * returns to is a same-origin path: it must start with a single "/" and must
 * not start with "//" (protocol-relative) or "/\" (which several browsers
 * normalise to "//"). Anything else falls back to the caller's default.
 *
 * @param {unknown} param the raw `?redirect=` value
 * @returns {string|null} a safe same-origin path, or null to use the default
 */
export function safeLoginRedirectTarget(param) {
  if (typeof param !== "string") return null;
  const target = param.trim();
  if (!target.startsWith("/")) return null; // absolute URL or scheme
  if (target.startsWith("//") || target.startsWith("/\\")) return null;
  if (target === PATH.LOGIN || target.startsWith(`${PATH.LOGIN}?`)) return null;
  return target;
}

/**
 * Is this axios error the server refusing an UNAUTHENTICATED caller?
 *
 * DRF answers an anonymous request with `401 {"detail": "Authentication
 * credentials were not provided."}` — note `detail`, not the `message` key the
 * app's own envelope uses, which is why an un-branched `onError` handler ends up
 * showing axios's raw `"Request failed with status code 401"` to the visitor.
 * Any 401 means "we do not know who you are (any more)", so the answer is always
 * the same: send them to sign in, and bring them back.
 *
 * @param {any} err axios error
 * @returns {boolean}
 */
export function isUnauthenticatedError(err) {
  return err?.response?.status === 401;
}

/**
 * Does this browser hold a session token?
 *
 * This is the single implementation of that read — `utils/helpers.js`
 * (`isUserLoggedIn` / `getLoggedInUserToken`) delegates to it. It lives HERE
 * rather than in helpers because helpers transitively imports the whole
 * segment-tools/editor tree (Froala included); a paywall overlay must not drag
 * a rich-text editor in just to ask "are you signed in?".
 *
 * Also hardened: the previous inline `JSON.parse(localStorage.getItem(...))`
 * throws on a corrupted entry. A malformed token store means "not signed in",
 * not a crash.
 *
 * @returns {boolean}
 */
export function isSignedIn() {
  return !!readAuthStore().token;
}

/**
 * The parsed `keyCloak` localStorage entry ({} when absent/corrupt).
 * @returns {{token?:string, user?:object}}
 */
export function readAuthStore() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    return JSON.parse(window.localStorage.getItem("keyCloak")) || {};
  } catch (e) {
    return {};
  }
}
